import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type OnlineAccessStatus = 'UNVERIFIED' | 'RETIRED';
export type OnlineAccessPermissionKind = 'ROOM_ENTRY' | 'PROPERTY_ROUTE' | 'ELEVATOR_FLOOR';
export type OnlineAccessTargetNamespace = 'CLUE_PLACE' | 'LORA_HOTEL_ROOM';
export interface OnlineAccessPermission {
  kind: OnlineAccessPermissionKind;
  target_namespace: OnlineAccessTargetNamespace;
  target_ref: string;
}
export interface RoomOnlineAccess {
  binding_id: string;
  property_id: string;
  room_id: string;
  revision: number;
  status: OnlineAccessStatus;
  issue_eligible: boolean;
  permission_items: OnlineAccessPermission[];
  reason: string;
  created_at: string;
  updated_at: string;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const hasKeys = (value: Record<string, unknown>, keys: readonly string[]) =>
  Object.keys(value).length === keys.length && keys.every((key) => key in value);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '출입 설정을 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
const kinds = ['ROOM_ENTRY', 'PROPERTY_ROUTE', 'ELEVATOR_FLOOR'] as const;
const namespaces = ['CLUE_PLACE', 'LORA_HOTEL_ROOM'] as const;
const clueRef = /^[A-Za-z0-9][A-Za-z0-9_-]{0,159}$/;
const loraRef = /^[A-Za-z0-9][A-Za-z0-9_-]{0,79}:[A-Za-z0-9][A-Za-z0-9_-]{0,79}$/;

function permission(value: unknown): value is OnlineAccessPermission {
  if (
    !record(value) ||
    !hasKeys(value, ['kind', 'target_namespace', 'target_ref']) ||
    !kinds.includes(value.kind as OnlineAccessPermissionKind) ||
    !namespaces.includes(value.target_namespace as OnlineAccessTargetNamespace) ||
    typeof value.target_ref !== 'string'
  )
    return false;
  if (value.target_namespace === 'CLUE_PLACE') return clueRef.test(value.target_ref);
  return value.kind === 'ROOM_ENTRY' && loraRef.test(value.target_ref);
}

function accessData(value: unknown, propertyId: string, roomId: string): RoomOnlineAccess | null {
  if (value === null) return null;
  const items =
    record(value) && Array.isArray(value.permission_items) ? value.permission_items : [];
  const permissionKeys = items.map((item) =>
    record(item)
      ? `${String(item.kind)}\u0000${String(item.target_namespace)}\u0000${String(item.target_ref)}`
      : '',
  );
  if (
    !record(value) ||
    !hasKeys(value, [
      'binding_id',
      'property_id',
      'room_id',
      'revision',
      'status',
      'issue_eligible',
      'permission_items',
      'reason',
      'created_at',
      'updated_at',
    ]) ||
    !isUuid(value.binding_id) ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    !isUuid(value.room_id) ||
    value.room_id.toLowerCase() !== roomId.toLowerCase() ||
    !Number.isSafeInteger(value.revision) ||
    Number(value.revision) < 1 ||
    !(['UNVERIFIED', 'RETIRED'] as unknown[]).includes(value.status) ||
    value.issue_eligible !== false ||
    !Array.isArray(value.permission_items) ||
    value.permission_items.length > 40 ||
    (value.status === 'RETIRED' && value.permission_items.length !== 0) ||
    !value.permission_items.every(permission) ||
    new Set(permissionKeys).size !== permissionKeys.length ||
    typeof value.reason !== 'string' ||
    !value.reason.trim() ||
    value.reason.trim().length > 240 ||
    !isOffsetDateTime(value.created_at) ||
    !isOffsetDateTime(value.updated_at) ||
    Date.parse(value.updated_at) < Date.parse(value.created_at)
  )
    throw invalid();
  return value as unknown as RoomOnlineAccess;
}

/** Read one selected Room. The Core endpoint rejects query parameters and has no request body. */
export async function readRoomOnlineAccess(
  client: ServiceClient,
  propertyId: string,
  roomId: string,
  signal?: AbortSignal,
): Promise<RoomOnlineAccess | null> {
  if (!isUuid(propertyId) || !isUuid(roomId)) throw invalid();
  const value = await client.get<unknown>(
    `admin/properties/${propertyId}/rooms/${roomId}/online-access`,
    { signal },
  );
  // The configured dataErrorEnvelope already unwraps the wire-level {data: ...} envelope.
  return accessData(value, propertyId, roomId);
}

export interface RoomOnlineAccessReplacement {
  expected_revision: number;
  permission_items: OnlineAccessPermission[];
  reason: string;
}
export interface RoomOnlineAccessRetirement {
  expected_revision: number;
  reason: string;
}

/** Closed DTO validation shared by the form and transport; no invented command key. */
export function validateRoomOnlineAccessWrite(
  method: 'PUT' | 'DELETE',
  body: RoomOnlineAccessReplacement | RoomOnlineAccessRetirement,
): void {
  if (
    !record(body) ||
    !hasKeys(
      body,
      method === 'PUT'
        ? ['expected_revision', 'permission_items', 'reason']
        : ['expected_revision', 'reason'],
    ) ||
    !Number.isSafeInteger(body.expected_revision) ||
    body.expected_revision < (method === 'PUT' ? 0 : 1) ||
    typeof body.reason !== 'string' ||
    !body.reason.trim() ||
    body.reason.length > 240
  )
    throw invalid();
  if (method === 'PUT') {
    const items = (body as RoomOnlineAccessReplacement).permission_items;
    if (
      !Array.isArray(items) ||
      items.length > 40 ||
      !items.every(permission) ||
      new Set(items.map((item) => `${item.kind}\0${item.target_namespace}\0${item.target_ref}`))
        .size !== items.length
    )
      throw invalid();
  }
}

export async function writeRoomOnlineAccess(
  client: ServiceClient,
  propertyId: string,
  roomId: string,
  method: 'PUT' | 'DELETE',
  body: RoomOnlineAccessReplacement | RoomOnlineAccessRetirement,
  signal?: AbortSignal,
): Promise<RoomOnlineAccess> {
  if (!isUuid(propertyId) || !isUuid(roomId)) throw invalid();
  validateRoomOnlineAccessWrite(method, body);
  const value = await client[method === 'PUT' ? 'put' : 'delete']<unknown>(
    `admin/properties/${propertyId}/rooms/${roomId}/online-access`,
    { json: body, signal, expectedStatus: 200 },
  );
  const result = accessData(value, propertyId, roomId);
  if (
    result?.status !== (method === 'PUT' ? 'UNVERIFIED' : 'RETIRED') ||
    (result.status === 'RETIRED' && result.permission_items.length > 0)
  )
    throw invalid();
  return result;
}
